import { btLogger } from "@api/internal/utils/helpers";
import { QueryBody } from "@api/internal/utils/types";
import { WebFetcher } from "@app/data";
import { RouteFactory } from "@app/routePaths";
import { fetchBTBackendAPI } from "@backend/utils/helpers";
import { TrimmedBreakingNewsArticle } from "@caas/types";
import { renderPageTitleV2 } from "@components/MetaTags/helpers";
import { navigationRequests } from "@pages/DefaultNavigationData.server.ts";
import { MyBtConfigResponse, MyBtUpdateResponse } from "@pages/MyBT/types";
import { sanitizeSectionObject } from "@pages/Section/helpers";
import { sanitizeFeedSectionItems } from "@pages/Section/utils/sanitizeFeedSectionItems";
import { FeedSectionData, SectionContext } from "@pages/Section/utils/types";
import { getTemplateGProps } from "@particles/card/templates/TemplateG/utils/helpers";
import { getTemplateHProps } from "@particles/card/templates/TemplateH/utils/helpers";
import { ResponseType, TRouteWithRedirect } from "@sphtech/web2-core/ssr";
import { OKTAUserTypeEnum } from "@store/useOKTAUserStore";
import type {
  ArticleDataObject,
  SanitizedSectionArticle,
  Tag,
} from "@typings/Cue";
import { tryCatch } from "@typings/TryCatch";
import { IS_PLAYWRIGHT } from "@util/constant";
import { textToSlug } from "@util/helpers";
import axios, { HttpStatusCode } from "axios";

export const sectionRequests = {
  fetchTrimmedBreakingNews: async (): Promise<TrimmedBreakingNewsArticle[]> => {
    try {
      const response = await axios.get("/_plat/api/v1/trimmed-breaking-news");
      return response.data;
    } catch (error) {
      return [];
    }
  },

  fetchArticlesByKeywords: async (tags: Tag[], size = 10) => {
    const urlPaths = tags.map((tag) => tag.urlPath);
    const response = await axios.post("/_plat/api/v1/mybt", { urlPaths, size });
    return response;
  },

  fetchMyBtFollowedKeywords: async (
    userType: OKTAUserTypeEnum,
    aovisitorid: string
  ) => {
    const response = await axios.get<MyBtConfigResponse>(
      "/_plat/api/v1/mybt/get-config",
      { headers: { usertype: userType, aovisitorid: aovisitorid } }
    );

    return <Tag[] | null>response.data.tags.map(({ name, id }) => {
      return { name: name, urlPath: `/keywords/${textToSlug(name)}`, id: id };
    });
  },

  updateMyBtFollowedKeywords: async (
    userType: OKTAUserTypeEnum = OKTAUserTypeEnum.ANONYMOUS,
    aovisitorid: string,
    tags: Tag[]
  ) => {
    if (userType == OKTAUserTypeEnum.ANONYMOUS)
      return <MyBtUpdateResponse>{
        code: 403,
        message: "Permission denied. User is anonymous.",
      };
    try {
      const response = await axios.post<MyBtUpdateResponse[]>(
        "/_plat/api/v1/mybt/update",
        { tags: tags },
        { headers: { usertype: userType, aovisitorid: aovisitorid } }
      );

      return response.data[0];
    } catch (error) {
      return <MyBtUpdateResponse>{
        code: 500,
        message: "Internal Server error.",
        errors: error,
      };
    }
  },

  deleteMyBtFollowedKeywords: async (
    userType: OKTAUserTypeEnum = OKTAUserTypeEnum.ANONYMOUS,
    aovisitorid: string,
    tags: Tag[]
  ) => {
    if (userType == OKTAUserTypeEnum.ANONYMOUS)
      return <MyBtUpdateResponse>{
        code: 403,
        message: "Permission denied. User is anonymous.",
      };
    try {
      const response = await axios.post<MyBtUpdateResponse[]>(
        "/_plat/api/v1/mybt/delete",
        { tags: tags },
        { headers: { usertype: userType, aovisitorid: aovisitorid } }
      );
      return response.data[0];
    } catch (error) {
      return <MyBtUpdateResponse>{
        code: 500,
        message: "Internal Server error.",
        errors: error,
      };
    }
  },

  fetchArticlesBySection: async ({
    sections = [],
    size = 10,
    page = 1,
    sortBy = "updated:desc",
  }: QueryBody): Promise<FeedSectionData | undefined> => {
    const payload = {
      sections: sections.join(","),
      size,
      page,
      sortBy,
      platform: "web",
      ...(IS_PLAYWRIGHT ? {} : { t: Date.now() }),
    };
    const { data: response, error } = await tryCatch(
      fetchBTBackendAPI<FeedSectionData>()("/v1/articles/sections", payload)
    );

    if (error) {
      btLogger({
        type: "error",
        message: `getArticlesBySection: ${JSON.stringify({ payload, error })}`,
      });

      return;
    }

    if (!response.success) return;

    return response.data;
  },
};

export const fetchSectionData: WebFetcher<
  TRouteWithRedirect<SectionContext, string>
> = async function ({ urlComponent }) {
  const urlPath =
    urlComponent && urlComponent.path
      ? urlComponent.path.replace(/\/+$/, "") // Remove trailing slash
      : "";

  const sectionUniqueName = urlPath
    .replace(/^\/+/, "") // Remove leading slash
    .replaceAll("/", "_") // Replace slashes with underscores
    .replace("_latest", ""); // Remove _latest

  const payload = {
    sections: [sectionUniqueName],
    size: 10,
    urlPath,
  };

  const sectionData = await sectionRequests.fetchArticlesBySection(payload);

  if (!sectionData) {
    return {
      type: ResponseType.CLIENT_ERROR,
      statusCode: HttpStatusCode.NotFound,
      payload: `Error fetching section data- url: ${urlPath} payload: ${JSON.stringify(payload)} data: ${JSON.stringify(sectionData)}`,
    };
  }

  const sectionEntity = sectionData.entity;

  if (typeof sectionEntity === undefined) {
    return {
      type: ResponseType.CLIENT_ERROR,
      statusCode: HttpStatusCode.NotFound,
      payload: `Section doesn't have entity tagged - url: ${urlPath} payload: ${JSON.stringify(payload)} data: ${JSON.stringify(sectionData)}`,
    };
  }

  const sectionEntityObject = sectionData.entity;
  const sectionObject = sanitizeSectionObject(urlPath, sectionEntityObject);

  const articles = sanitizeFeedSectionItems(sectionData.items || []);

  const isMetaDisabled = [
    RouteFactory.globalEnterpriseLatest,
    RouteFactory.aseanBusinessLatest,
    RouteFactory.sgsmeLatest,
  ].includes(urlPath);

  const templateGProps = getTemplateGProps(
    articles.slice(1, 10),
    !isMetaDisabled
  );
  const templateHProps = getTemplateHProps(
    articles.slice(0, 1),
    !isMetaDisabled
  );

  const seoFooterData = await navigationRequests.fetchSeoFooter();

  return {
    type: ResponseType.SUCCESS,
    statusCode: HttpStatusCode.Ok,
    payload: {
      kind: "section",
      data: {
        entity: sectionObject,
        relativePath: urlPath,
        title: renderPageTitleV2({
          kind: "section",
          sectionName: sectionObject.name,
          sectionParentName: sectionObject.parent?.name,
        }),
        overview: [...templateHProps, ...templateGProps],
        seoFooterData,
      },
    },
  };
};

export const fetchMoreStoriesData = async function (
  category: string | string[],
  parentCategory: string,
  page: number
): Promise<SanitizedSectionArticle[]> {
  try {
    const response = await axios.post<SanitizedSectionArticle[]>(
      "/_plat/api/v1/more-stories",
      {
        category, // "companies-markets_banking-finance" || "food-drink"
        parentCategory, // "companies-markets" || "keywords"
        size: 10,
        page,
      }
    );

    return response.data || [];
  } catch (error) {
    return [];
  }
};

export const fetchMoreBrandedContentData = async function (
  article: ArticleDataObject
): Promise<ArticleDataObject[]> {
  try {
    const response = await axios.post("/_plat/api/v1/more-stories-by-kicker", {
      kicker: "Branded Content",
      sort: article.sort,
      size: 10,
    });
    return response.data;
  } catch (error) {
    return [];
  }
};
